import { useMemo } from "react";
import { Column, useBlockLayout, useSortBy, useTable } from "react-table";
import { useSticky } from "react-table-sticky";

import { TaskStatusResponseData, Tasks } from "../../../types/api";
import Text from "../Text";
import { COLORS } from "../../../styles/colors";
import { FONT_WEIGHT } from "../../../styles/fonts";

const SummaryBox = ({
  searchResults,
  totalRecords,
  // limit,
  // fetchRefreshResults,
  alertColor,
}: {
  searchResults: TaskStatusResponseData;
  totalRecords: number;
  // limit: number;
  // fetchRefreshResults: (request: TaskStatusRequest) => void;
  alertColor?: boolean;
}): JSX.Element => {
  const COLUMNS = useMemo<Column<Tasks>[]>(
    () => [
      {
        accessor: "server",
        Header: () => (
          <span
            style={{
              fontWeight: FONT_WEIGHT.medium,
              fontSize: "0.925rem",
              color: COLORS.black1,
            }}
          >
            SERVER
          </span>
        ),
        Cell: ({ value }) => <Text>{value || "--"}</Text>,
        width: "15%",
      },
      {
        accessor: "description",
        Header: () => (
          <span
            style={{
              fontWeight: FONT_WEIGHT.medium,
              fontSize: "0.925rem",
              color: COLORS.black1,
            }}
          >
            DL_DESCRIPTION
          </span>
        ),
        Cell: ({ value }) => <Text>{value || "--"}</Text>,
        width: "20%",
      },
      {
        accessor: "taskDone",
        Header: () => (
          <span
            style={{
              fontWeight: FONT_WEIGHT.medium,
              fontSize: "0.925rem",
              color: COLORS.black1,
            }}
          >
            TASK_DONE
          </span>
        ),
        marginBottom: "2rem",
        Cell: ({ value }) => <Text>{value || "--"}</Text>,
        width: "12%",
      },
      {
        accessor: "status",
        Header: () => (
          <span
            style={{
              fontWeight: FONT_WEIGHT.medium,
              fontSize: "0.925rem",
              color: COLORS.black1,
            }}
          >
            STATUS
          </span>
        ),
        Cell: ({ value }) => <Text>{value || "--"}</Text>,
        width: "15%",
      },
      {
        accessor: "timeDifference",
        Header: () => (
          <span
            style={{
              fontWeight: FONT_WEIGHT.medium,
              fontSize: "0.925rem",
              color: COLORS.black1,
            }}
          >
            TIME_DIFF
          </span>
        ),
        Cell: ({ value }) => <Text>{value || "--"}</Text>,
        width: "10%",
      },
      {
        accessor: "lastAction",
        Header: () => (
          <span
            style={{
              fontWeight: FONT_WEIGHT.medium,
              fontSize: "0.925rem",
              color: COLORS.black1,
            }}
          >
            LAST_ACTION
          </span>
        ),
        Cell: ({ value }) => <Text>{value || "--"}</Text>,
        width: "12%",
      },
      {
        accessor: "nextAction",
        Header: () => (
          <span
            style={{
              fontWeight: FONT_WEIGHT.medium,
              fontSize: "0.925rem",
              color: COLORS.black1,
            }}
          >
            NEXT_ACTION
          </span>
        ),
        Cell: ({ value }) => <Text>{value || "--"}</Text>,
        width: "16%",
      },
    ],
    [],
  );
  const { rows, headerGroups, getTableProps, getTableBodyProps, prepareRow } =
    useTable(
      {
        columns: COLUMNS,
        data: searchResults,
      },
      useSortBy,
      useSticky,
      useBlockLayout,
    );

  return (
    <div style={{ padding: "0 1rem" }}>
      {totalRecords > 0 ? (
        <>
          <table
            {...getTableProps()}
            style={{
              borderRadius: "0.625rem",
              fontSize: "2rem",
              borderTop: `0.0625rem solid ${COLORS.grey5}`,
              borderRight: `0.0625rem solid ${COLORS.grey5}`,
              borderLeft: `0.0625rem solid ${COLORS.grey5}`,
              // overflowX: 'scroll',
              borderCollapse: "collapse",
              // display: 'block',
              border: "0",
              borderSpacing: "0",
              cursor: "default",
              width: "100%",
            }}
          >
            <thead
              style={{
                position: "relative",
                top: 0,
                width: "100%",
                zIndex: 5,
              }}
            >
              {headerGroups.map((headerGroup) => {
                const { key, ...restHeaderGroupProps } =
                  headerGroup.getHeaderGroupProps();
                return (
                  <tr
                    key={key}
                    {...restHeaderGroupProps}
                    style={{
                      height: "3.75rem",
                      lineHeight: "3.125rem",
                    }}
                  >
                    {headerGroup.headers.map((column) => {
                      const { key: thKey, ...restThProps } =
                        column.getHeaderProps(column.getSortByToggleProps());

                      return (
                        <th
                          key={thKey}
                          {...restThProps}
                          style={{
                            width: column.width,
                            letterSpacing: "0.019rem",
                            paddingLeft: "0.5rem",
                            color: COLORS.black,
                            fontSize: "0.875rem",
                            fontWeight: FONT_WEIGHT.regular,
                            padding: "0.3125rem",
                            overflow: "hidden",
                          }}
                        >
                          {column.render("Header")}
                        </th>
                      );
                    })}
                  </tr>
                );
              })}
            </thead>

            <tbody {...getTableBodyProps()} id="tableBody">
              {rows?.map((row) => {
                prepareRow(row);
                const { key, ...restRowProps } = row.getRowProps();
                return (
                  <tr
                    key={key}
                    id="tableRow"
                    {...restRowProps}
                    style={{
                      fontSize: "0.875rem",
                      display: "inline-block !important",
                      width: "100%",
                      letterSpacing: "0.019rem",
                      borderBottom: `0.0625rem solid ${COLORS.grey10}`,
                    }}
                  >
                    {row.cells.map((cell) => {
                      const { key: tdKey, ...restCellProps } =
                        cell.getCellProps();
                      return (
                        <td
                          className={
                            alertColor &&
                            (cell?.row?.values?.status === "Alert" ||
                              cell?.row?.values?.status === "Not Submitted") &&
                            cell?.row?.values?.description !==
                              "Overdue Loan Notification" &&
                            cell?.row?.values?.description !==
                              "ATA Defect Alert - Fleet Level" &&
                            cell?.row?.values?.description !==
                              "Message Processor - Reqs Download"
                              ? "yellow"
                              : ""
                          }
                          key={tdKey}
                          id="tableData"
                          {...restCellProps}
                          style={{
                            overflowWrap: "break-word",
                            padding: "0.5rem 0.4rem 0.5rem 0.5rem",
                            letterSpacing: "0.019rem",
                            width: cell.column.width,
                            minWidth: cell.column.minWidth,
                          }}
                        >
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      ) : null}
    </div>
  );
};

export default SummaryBox;
